// 주문 정보 섹션 컴포넌트
import type { OrderData } from './types';
import { receiptTranslations } from './constants';
import { useLanguage } from '@/contexts/AppContext';
import { DEFAULT_STORE_TIMEZONE } from '@/utils/menuAvailability';

interface OrderInfoProps {
  orderData: OrderData;
}

// 날짜/시간 포맷 헬퍼.
// #294: undefined-안전 시그니처. 이전엔 호출부에서 `|| new Date()` 폴백을 썼으나,
// 누락된 주문 시각이 "현재 시각"으로 찍히는 semantic 회귀가 있었음(영수증이
// 법적/회계적 산출물임을 고려하면 위험). 없으면 빈 문자열 반환.
// #1816: timeZone 미지정 시 고객 기기 로컬 TZ 로 렌더돼 해외/UTC 기기에서 매장 시각과
// 어긋남. 영수증은 법적/회계 산출물이므로 매장 타임존(멕시코)으로 고정한다.
const formatDate = (date: Date | undefined) => {
  if (!date) return '';
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    timeZone: DEFAULT_STORE_TIMEZONE,
  });
};

const formatTime = (date: Date | undefined) => {
  if (!date) return '';
  return date.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: DEFAULT_STORE_TIMEZONE,
  });
};

export function OrderInfo({ orderData }: OrderInfoProps) {
  // #522: 15 locale 전수 lookup — Locale 단일 출처 정합.
  const { locale } = useLanguage();
  const t = receiptTranslations[locale];

  return (
    <div className="px-5 py-4 border-b border-border space-y-2.5">
      {/* 주문번호 */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{t.orderNumber}</span>
        {/* #632: 더미 번호('14901') fallback 제거 — 미상이면 '-' (가짜 주문번호 노출 금지) */}
        <span className="text-sm font-bold text-primary">#{orderData.orderId || '-'}</span>
      </div>

      {/* 날짜/시간 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{t.date}</span>
          <span className="text-xs font-medium text-foreground">
            {formatDate(orderData.timestamp)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{t.time}</span>
          <span className="text-xs font-medium text-foreground">
            {formatTime(orderData.timestamp)}
          </span>
        </div>
      </div>
    </div>
  );
}
